import _ from 'lodash'
import Mustache from 'mustache'
import { call, put, select, takeLatest } from 'redux-saga/effects'

import DEFAULTS from 'core/data-model/constants/defaults'
import CONSTANTS from 'core/data-model/constants/messages'
import MESSAGES from 'core/data-model/constants/validation-messages'
import VALUES from 'core/data-model/constants/values'
import { renderUnit } from 'core/service/insured/age'
import { isValidAge } from 'core/service/insured/birthdate'
import { UPDATE_BIRTHDATE } from 'quick-quote/insured-information/actions'
import {
  getAge,
  getAllNatureOfDutyCodes,
  getAllPayerNatureOfDutyCodes,
  getInsured,
  getPayerAge,
  getPayerGender,
} from 'quick-quote/insured-information/selectors'
import { updateBasicPlanErrorMessage } from 'quick-quote/product-common/coverage-plan/actions'
import { updateCoveragePlanState } from 'quick-quote/product-common/coverage-plan/sagas'
import { getAvailableRiders } from 'quick-quote/product-common/coverage-plan/selectors'
import {
  getLoanPeriod,
  getCoveragePeriod,
  getLoanAmount,
  getLoan,
  getInterestRateValue,
} from 'quick-quote/product-mrta/coverage-plan/selectors'
import {
  getDisplayProducts,
  getSelectedDisplayProduct,
  getSelectedDisplayProductQuery,
} from 'quick-quote/product-selection/selectors'

import {
  IHEALTHY_ULTRA_SELECT_BASIC_PLAN,
  selectBasicPlan,
  IHEALTHY_ULTRA_UPDATE_SELECTED_BASIC_PLAN,
  VALIDATE_BASIC_PLAN,
  validateBasicPlan,
} from './actions'
import { getSelectedModelFactorID } from './selectors'

/**
 * createProductIHealthyUltraCoverageFunc
 * @param {ReturnType<import('core/service/basic-plan/basic-plan').createBasicPlanService>} basicPlanService
 * @param {ReturnType<import('quick-quote/product-common/coverage-plan/sagas').createProductCommonCoverageFunc>} productCommonCoverage
 */
export function createProductIHealthyUltraCoverageFunc(basicPlanService, productCommonCoverage) {
  const getProductFromDefaultListIHealthyUltraProduct = (basicPlanCode) => {
    return _.find(DEFAULTS.DEFAULT_IHEALTHY_ULTRA_PRODUCT_LIST, { code: basicPlanCode })
  }

  const validateProductMultiBasicPlanCode = (basicPlanCode, insureAge) => {
    if (VALUES.IHEALTHY_ULTRA_LIST_PRODUCT_MULTI_PLANCODE.includes(basicPlanCode)) {
      const product = getProductFromDefaultListIHealthyUltraProduct(basicPlanCode)
      const productSelected = DEFAULTS.DEFAULT_IHEALTHY_ULTRA_PRODUCT_LIST.filter(
        (p) =>
          p.productName === product.productName &&
          insureAge.value >= p.entryAge.minimum.value &&
          insureAge.value <= p.entryAge.maximum.value
      )
      if (productSelected.length) {
        return productSelected[0].code
      } else {
        return basicPlanCode
      }
    } else {
      return basicPlanCode
    }
  }

  const onUpdateSelectedBasicPlan = function*(action) {
    // const basicPlanCode = action.payload
    const insureAge = yield select(getAge)
    const displayProducts = yield select(getDisplayProducts)
    const basicPlanCode = validateProductMultiBasicPlanCode(action.payload, insureAge)
    const productSelected = _.find(displayProducts, { code: basicPlanCode })
    if (productSelected) {
      yield put(selectBasicPlan(productSelected))
    }
  }

  const generateCoveragePlanFromBasicPlanChange = function*() {
    const displayProductQuery = yield select(getSelectedDisplayProductQuery)
    const natureOfDutyCodes = yield select(getAllNatureOfDutyCodes)
    const payerNatureOfDutyCodes = yield select(getAllPayerNatureOfDutyCodes)
    const insured = yield select(getInsured)
    const modelFactorID = yield select(getSelectedModelFactorID)
    const availableRiders = yield select(getAvailableRiders)
    const payerAge = yield select(getPayerAge)
    const payerGender = yield select(getPayerGender)
    const loanPeriod = yield select(getLoanPeriod)
    const loanAmount = yield select(getLoanAmount)
    const coveragePeriod = yield select(getCoveragePeriod)
    const loan = yield select(getLoan)
    const interestRate = yield select(getInterestRateValue)
    const newValue = yield call(basicPlanService.calculateCoveragePlanFromBasicPlanChange, {
      displayProductQuery,
      insured,
      natureOfDutyCodes,
      payerNatureOfDutyCodes,
      modelFactorID,
      availableRiders,
      payerAge,
      payerGender,
      loanPeriod,
      coveragePeriod,
      loanAmount,
      loan,
      interestRate,
    })
    yield call(updateCoveragePlanState, newValue)
    yield put(validateBasicPlan())
  }

  const getValidationMessage = (displayProduct, age) => {
    const { entryAge } = displayProduct
    if (!entryAge || isValidAge(age.value, age.unit, entryAge)) {
      return ''
    }
    return Mustache.render(MESSAGES.RULE_AGE, {
      owner: CONSTANTS.INSURED_PERSON,
      minimumAge: `${entryAge.minimum.value} ${renderUnit(entryAge.minimum.unit)}`,
      // minimumAge: `${_checkRenderMinAge(code, entryAge.minimum.value)} ${renderUnit(entryAge.minimum.unit)}`,
      maximumAge: `${entryAge.maximum.value} ${renderUnit(entryAge.maximum.unit)}`,
    })
  }

  const onValidateBasicPlan = function*() {
    const selectedDisplayProduct = yield select(getSelectedDisplayProduct)
    if (selectedDisplayProduct.category !== VALUES.IHEALTHY_ULTRA) {
      return
    }
    const age = yield select(getAge)
    const message = yield call(getValidationMessage, selectedDisplayProduct, age)
    return yield put(updateBasicPlanErrorMessage(message))
  }

  return {
    getProductFromDefaultListIHealthyUltraProduct,
    validateProductMultiBasicPlanCode,
    onUpdateSelectedBasicPlan,
    generateCoveragePlanFromBasicPlanChange,
    onValidateBasicPlan,
  }
}

/**
 * @param serviceWrapper {Awaited<ReturnType<import('core/service-wrapper/wrapper').buildServiceWrapper>>}
 */
export function* sagas(serviceWrapper) {
  const { I_HEALTHY_ULTRA } = serviceWrapper.products

  yield [
    takeLatest(IHEALTHY_ULTRA_UPDATE_SELECTED_BASIC_PLAN, I_HEALTHY_ULTRA.onUpdateSelectedBasicPlan),
    takeLatest(IHEALTHY_ULTRA_SELECT_BASIC_PLAN, I_HEALTHY_ULTRA.generateCoveragePlanFromBasicPlanChange),
    takeLatest(VALIDATE_BASIC_PLAN, I_HEALTHY_ULTRA.onValidateBasicPlan),
    takeLatest(UPDATE_BIRTHDATE, I_HEALTHY_ULTRA.onValidateBasicPlan),
  ]
}
